<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()

// Lazy-resolve layout components so each is code-split only when a route asks for it.
// Blank.vue is the no-layout wrapper (renders slot only).
const layoutMap = {
  default: defineAsyncComponent(() => import('~/layouts/default.vue')),
  docs:    defineAsyncComponent(() => import('~/layouts/docs.vue')),
  blank:   defineAsyncComponent(() => import('~/layouts/Blank.vue')),
} as const

// Resolve layout key from meta (undefined → blank = no layout).
const layoutKey = computed(() => {
  const meta = route.meta.layout as 'default' | 'docs' | undefined
  return meta ?? 'blank'
})

const layoutComponent = computed(() => layoutMap[layoutKey.value])

// Keyed remount: use the full path as key so structurally-different pages (e.g. /app
// vs /auth, both no-layout) fully remount instead of Vue reusing the previous root
// element — which corrupted the /auth layout on the /app→/auth redirect.
const pageKey = computed(() => route.fullPath)
</script>

<template>
  <div id="app-root">
    <!-- Dynamic layout wrapper selected by route.meta.layout.
         Blank.vue is a pass-through when no layout is requested. -->
    <component :is="layoutComponent">
      <!-- AppBootGate prevents protected content from flashing before auth resolves.
           It shows a neutral loading surface on requiresAuth routes while the session
           store determines authentication status. -->
      <AppBootGate>
        <!-- Keyed RouterView ensures full remount when the route path changes
             (critical for no-layout pages like /app ↔ /auth). -->
        <RouterView :key="pageKey" />
      </AppBootGate>
    </component>
  </div>
</template>

<style scoped>
#app-root {
  min-height: 100vh;
  background: var(--dq-bg);
  color: var(--dq-text);
  font-family: var(--dq-font-sans);
}
</style>
